/**
 * Analytics utility for funnel tracking
 * Uses Google Analytics 4 (gtag) + Google Ads conversion tracking + Meta Pixel
 *
 * Key architecture decisions (learned from Vore):
 * - GA4 purchase events scoped with `send_to: <GA4 id>` to avoid double-counting in Google Ads
 * - Google Ads conversion fired separately with `send_to: 'AW-...'` — immediate
 *   purchases only; trials never reach the Ads purchase action (plan §A3)
 * - sessionStorage dedup prevents duplicate conversion fires
 * - `event_callback` + timeout ensures beacon sends before SPA navigation
 *
 * Meta:
 * - Pixel snippet lives in index.html; fires PageView automatically on load
 * - Custom events use the same kp_event_* naming as the RC→Meta integration
 *   so health-flagged accounts can map to Custom Conversions without exposing
 *   standard event names. kp_event_06 = web funnel Purchase.
 * - Purchase / StartTrial / kp_event_09 fire browser-side here AND server-side
 *   from the Meta CAPI outbox (functions/src/metaCapiOutbox.ts). Same eventID
 *   on both → Meta dedupes (per event name).
 *
 * Every GA4 event and Meta custom-data object carries experiment_version and
 * enrollment_id (plan §A4). In a QA session (lib/qa.ts) GA4 goes to the QA
 * property (or nowhere) and the Google Ads tag is not loaded.
 */

import { getAttribution, noteBrowserIds } from './attribution'
import { getVariant } from './variant'
import { getEnrollment } from './enrollment'
import { analyticsState } from './qa'
import { isTrialPlan, planPurchaseAnalytics, withEnrollmentParams } from './trackingCore'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    fbq?: (...args: unknown[]) => void
  }
}

// Web funnel custom events. RC's integration owns kp_event_01..05 (app subscription events).
// Web funnel uses 06..08 so they're distinct and can be set up as Custom Conversions
// independently. Standard events (Lead, ViewContent, etc.) aren't reliably available for
// optimization in Special Ad Category accounts, which is why we also fire custom equivalents
// at the same trigger points.
//   kp_event_06 = Purchase (lowest volume, highest signal — optimize on this at scale)
//   kp_event_07 = Email Submitted (highest volume — primary optimization event at low spend)
//   kp_event_08 = Checkout Initiated (mid-funnel — switch up to this once 07 hits ~50/wk)
//   kp_event_09 = Trial Started (browser twin of the server outbox event, same eventID)
export const META_PURCHASE_EVENT = 'kp_event_06'
export const META_EMAIL_EVENT = 'kp_event_07'
export const META_CHECKOUT_EVENT = 'kp_event_08'
export const META_TRIAL_EVENT = 'kp_event_09'

const GOOGLE_ADS_PURCHASE_CONVERSION = 'AW-17896777113/auA1CN6BrOwbEJnL7NVC'

/** experiment_version + enrollment_id on every funnel event (GA4 and Meta). */
function withContext<T extends Record<string, unknown>>(params?: T) {
  noteBrowserIds() // so a checkout after a mid-page cookie loss still carries fbp/fbc
  return withEnrollmentParams(params, getEnrollment())
}

export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, withContext(params))
  }
}

export function trackStepView(stepName: string, stepNumber: number) {
  trackEvent(`funnel_step_${stepName}`, { step_number: stepNumber })

  if (stepName === 'email') {
    trackEvent('funnel_milestone_email_reached')
  } else if (stepName === 'offer') {
    trackEvent('funnel_milestone_offer_reached')
  } else if (stepName === 'createAccount') {
    trackEvent('funnel_milestone_checkout_reached')
  }
}

export function trackEmailEntered() {
  trackEvent('funnel_email_entered', { has_email: true })
}

export function trackSubscriptionBlockShown(platform: string | null) {
  trackEvent('funnel_subscription_block_shown', { existing_platform: platform || 'unknown' })
}

/** A returning buyer with a parked web purchase was sent to account setup (no second offer). */
export function trackPaidSetupResumed() {
  trackEvent('funnel_paid_setup_resumed', {})
}

/** The paid-setup account step attached the waiting purchase. */
export function trackPaidSetupClaimed(method: 'email' | 'google') {
  trackEvent('funnel_paid_setup_claimed', { method })
}

export function trackSubscriptionBlockAction(action: 'app_store' | 'play_store' | 'use_different_email') {
  trackEvent('funnel_subscription_block_action', { action })
}

export function trackCheckoutStarted(planId: string, price: number) {
  trackEvent('funnel_checkout_started', {
    plan_id: planId,
    value: price,
    currency: 'USD',
  })
}

const PURCHASE_TRACKED_KEY = 'kidneypal_purchase_tracked'
const PURCHASE_DATA_KEY = 'kidneypal_purchase_data'

/**
 * Save purchase data for fallback tracking on subsequent pages
 */
export function savePurchaseData(transactionId: string, planId: string, price: number, isTrial: boolean, email?: string) {
  try {
    sessionStorage.setItem(PURCHASE_DATA_KEY, JSON.stringify({ transactionId, planId, price, isTrial, email }))
  } catch {}
}

/**
 * Fire the checkout conversion with dedup protection (plan §A3):
 *   - trial      → GA4 `funnel_trial_started` (value 0). No Google Ads conversion.
 *   - immediate  → GA4 `funnel_immediate_purchase` (value = price) + the Google
 *                  Ads purchase conversion (skipped when the Ads tag isn't
 *                  loaded, i.e. QA sessions).
 * `funnel_purchase_complete` is retired. Returns a promise that resolves once
 * the Google Ads beacon is sent (or after a timeout) so callers can await it
 * before navigating away.
 */
export function firePurchaseConversion(
  transactionId: string,
  planId: string,
  price: number,
  isTrial: boolean,
  email?: string,
): Promise<void> {
  // Skip if already fired for this transaction
  try {
    if (sessionStorage.getItem(PURCHASE_TRACKED_KEY) === transactionId) {
      return Promise.resolve()
    }
  } catch {}

  const { ga4Id, adsEnabled } = analyticsState()
  const plan = planPurchaseAnalytics({ isTrial, price, adsEnabled })

  // GA4 custom event (scoped to the GA4 property only to avoid double-counting in Google Ads)
  const attribution = getAttribution()
  if (typeof window !== 'undefined' && window.gtag && ga4Id) {
    window.gtag('event', plan.ga4Event, withContext({
      send_to: ga4Id,
      transaction_id: transactionId,
      plan_id: planId,
      value: plan.ga4Value,
      currency: 'USD',
      traffic_source: attribution?.utm_source || (attribution?.gclid ? 'google_ads' : 'direct'),
      traffic_medium: attribution?.utm_medium || (attribution?.gclid ? 'cpc' : 'none'),
      traffic_campaign: attribution?.utm_campaign || '',
    }))
  }

  // Google Ads conversion (immediate purchases only) — wait for the beacon
  // to send before resolving.
  const conversionSent = new Promise<void>((resolve) => {
    if (plan.fireAdsConversion && typeof window !== 'undefined' && window.gtag) {
      // Enhanced conversion user data (hashed automatically by gtag)
      if (email) window.gtag('set', 'user_data', { email })
      const timeout = setTimeout(resolve, 2000)

      window.gtag('event', 'conversion', {
        'send_to': GOOGLE_ADS_PURCHASE_CONVERSION,
        'value': price,
        'currency': 'USD',
        'transaction_id': transactionId,
        'event_callback': () => {
          clearTimeout(timeout)
          resolve()
        },
      })
    } else {
      resolve()
    }
  })

  // Mark as tracked for this session
  try {
    sessionStorage.setItem(PURCHASE_TRACKED_KEY, transactionId)
  } catch {}

  return conversionSent
}

/**
 * Fallback: attempt to fire conversion from saved purchase data.
 * Called on CreateAccountStep mount in case OfferStep tracking failed. Same
 * trial / immediate split as the primary path (data saved before this
 * change has no isTrial — derived from the plan).
 */
export function fireConversionFallback() {
  try {
    const raw = sessionStorage.getItem(PURCHASE_DATA_KEY)
    if (!raw) return
    const { transactionId, planId, price, isTrial, email } = JSON.parse(raw)
    if (!transactionId) return
    const trial = typeof isTrial === 'boolean' ? isTrial : isTrialPlan(planId)
    firePurchaseConversion(transactionId, planId, price, trial, email)
  } catch {}
}

// ───────────────────────── Meta Pixel ─────────────────────────

function fbq(...args: unknown[]) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(...args)
  }
}

/**
 * Generate a UUID for Meta event_id dedup. Browser fires it here, server replays
 * the same value via CAPI — Meta dedupes by event_id (per event name) within its dedupe window.
 */
export function newMetaEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `kp-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function trackMetaViewContent() {
  fbq('track', 'ViewContent', withContext({ content_name: 'funnel_landing', funnel_variant: getVariant() }))
}

export function trackMetaLead() {
  fbq('track', 'Lead', withContext({ content_name: 'funnel_email_submitted', funnel_variant: getVariant() }))
}

export function trackMetaInitiateCheckout(planId: string, price: number) {
  fbq('track', 'InitiateCheckout', withContext({
    content_ids: [planId],
    value: price,
    currency: 'USD',
    funnel_variant: getVariant(),
  }))
}

/**
 * Fire the web custom Email Submitted event. Same trigger as `trackMetaLead`.
 * Historical context: when KidneyPal was in Special Ad Category (Health/Wellness),
 * standard events weren't reliably usable for optimization, so we mirrored every
 * standard fire with a custom kp_event_*. SAC was lifted 2026-06-02; we keep the
 * custom fires for backwards-compat with already-configured Custom Conversions
 * but ad campaigns going forward can optimize on the standard event directly.
 */
export function fireMetaEmailSubmitted() {
  fbq('trackCustom', META_EMAIL_EVENT, withContext({
    content_name: 'funnel_email_submitted',
    funnel_variant: getVariant(),
  }))
}

/**
 * Fire the web custom Checkout Initiated event. Same trigger as
 * `trackMetaInitiateCheckout`. See fireMetaEmailSubmitted for SAC context.
 */
export function fireMetaCheckoutInitiated(planId: string, price: number) {
  fbq('trackCustom', META_CHECKOUT_EVENT, withContext({
    content_ids: [planId],
    value: price,
    currency: 'USD',
    funnel_variant: getVariant(),
  }))
}

/**
 * Fire BOTH the standard Purchase event AND the custom kp_event_06. The eventId
 * is shared between them and forwarded in Paddle custom_data.meta_event_id, so
 * the server outbox (charge_<txn>) replays the same ID for dedup.
 *
 * The standard Purchase event is the optimization target going forward (SAC was
 * lifted 2026-06-02). The custom kp_event_06 fire is retained for backwards-compat
 * with already-configured Custom Conversions and historical reporting continuity.
 */
export function fireMetaPurchase(eventId: string, planId: string, price: number) {
  const params = withContext({
    content_ids: [planId],
    value: price,
    currency: 'USD',
    funnel_variant: getVariant(),
  })
  fbq('trackCustom', META_PURCHASE_EVENT, params, { eventID: eventId })
  fbq('track', 'Purchase', params, { eventID: eventId })
}

/**
 * Fire StartTrial when a user starts a free-trial subscription (the 1yr plan).
 *
 * `value` is the predicted post-trial subscription price (e.g. $59.99 for the
 * 1yr plan) — not money charged yet. Meta uses this as predicted LTV so it can
 * rank trial-starters by expected value during optimization. Without it, Meta's
 * Diagnostics flags "missing valid price information" and "all events sending
 * same price" (modeled +5% ROAS impact when fixed).
 *
 * Also fires the custom kp_event_09 (plan §A2) with the SAME eventID: the
 * server sends StartTrial + kp_event_09 from the Meta CAPI outbox using the
 * checkout's meta_event_id, and Meta dedupes each name against its browser
 * twin. (Dedupe only merges identical event names, so StartTrial and
 * kp_event_09 stay separate by design.)
 *
 * The actual Purchase event fires later from the server outbox when Paddle
 * reports the first real charge ~7 days after trial start.
 */
export function fireMetaStartTrial(eventId: string, planId: string, predictedValue: number) {
  const params = withContext({
    content_ids: [planId],
    currency: 'USD',
    value: predictedValue,
    funnel_variant: getVariant(),
  })
  fbq('track', 'StartTrial', params, { eventID: eventId })
  fbq('trackCustom', META_TRIAL_EVENT, params, { eventID: eventId })
}
